/**
 * Phase 20 / Task 20.7 — the import wizard's calls.
 *
 * Every route here takes the batch id in its PATH, which `useApiMutation` cannot
 * do (its path is fixed when the hook is created), so the wizard goes through
 * `importRequest` instead.
 *
 * ── DEC-201: a mutation always carries a body ──────────────────────────────
 * `importRequest` serialises `body ?? {}` for every non-GET JSON call. A
 * body-less POST with a JSON content-type is rejected by Fastify before the
 * handler runs, and surfaces as a 500 — which is how Approve was broken. The
 * preview, error-file and undo routes take no input at all; they still get `{}`.
 * A test asserts the `{}`.
 *
 * ── Error text ─────────────────────────────────────────────────────────────
 * Several import refusals carry their explanation in `fields[0].reason` rather
 * than the catalog message — the dry-run cap (DEC-267) and a purged batch
 * (20.6f) both say what to DO there. `readErrorMessage` prefers that sentence.
 */

import type { ERROR_CATALOG } from '@lodex-pro/shared';
import { authedFetch, type WebApiOpts } from '@lodex-pro/web-shared';

import type {
  ApplyMappingBody,
  ApplyMappingResult,
  BatchPreview,
  ColumnMapping,
  CommitMode,
  CommitQueued,
  CommitStatus,
  ImportBatchDetail,
  ImportBatchSummary,
  MappingProfile,
  MappingResponse,
  MappingValidation,
  ReportDownload,
  SheetDecision,
  SheetsUpdate,
  UndoReport,
  UploadResult,
} from './types';

/** §80 / `MAX_UPLOAD_BYTES` in api/src/import-intake/sniff.ts. */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

/** An import call that failed, with the most useful sentence the API gave. */
export class ImportApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, code: string | null, message: string) {
    super(message);
    this.name = 'ImportApiError';
    this.status = status;
    this.code = code;
    Object.setPrototypeOf(this, ImportApiError.prototype);
  }
}

interface ErrorEnvelope {
  readonly error?: {
    readonly code?: unknown;
    readonly message?: unknown;
    readonly fields?: ReadonlyArray<{ readonly reason?: unknown }>;
  };
}

const GENERIC_FAILURE = 'Something went wrong. Try again.';

/** The field reason when there is one, else the catalog message, else a generic line. */
export async function readErrorMessage(
  res: Response,
): Promise<{ code: string | null; message: string }> {
  try {
    const body = (await res.json()) as ErrorEnvelope;
    const code = typeof body.error?.code === 'string' ? body.error.code : null;
    const reason = body.error?.fields?.[0]?.reason;
    // A Zod issue's reason is a code (`invalid_string`); the import routes' own
    // reasons are sentences. Only a sentence is worth showing a hauler.
    if (typeof reason === 'string' && reason.includes(' ')) return { code, message: reason };
    const message = body.error?.message;
    return { code, message: typeof message === 'string' ? message : GENERIC_FAILURE };
  } catch {
    return { code: null, message: GENERIC_FAILURE };
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH';

/**
 * One JSON call. Non-GET calls ALWAYS send a body — `{}` when the caller passes
 * none (DEC-201).
 */
export async function importRequest<T>(
  opts: WebApiOpts,
  method: Method,
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit =
    method === 'GET' ? { method } : { method, body: JSON.stringify(body ?? {}) };
  const res = await authedFetch(opts, path, init);
  if (!res.ok) {
    const { code, message } = await readErrorMessage(res);
    throw new ImportApiError(res.status, code, message);
  }
  return (await res.json()) as T;
}

export const uploadImportFile = async (opts: WebApiOpts, file: File): Promise<UploadResult> => {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await authedFetch(opts, '/v1/imports', { method: 'POST', body: form });
  if (!res.ok) {
    if (res.status === 413) {
      throw new ImportApiError(413, 'PAYLOAD_TOO_LARGE', 'That file is over the 50 MB limit.');
    }
    const { code, message } = await readErrorMessage(res);
    throw new ImportApiError(res.status, code, message);
  }
  return (await res.json()) as UploadResult;
};

const batchPath = (batchId: string, suffix: string): string =>
  `/v1/imports/${encodeURIComponent(batchId)}${suffix}`;

export const getMapping = (opts: WebApiOpts, batchId: string): Promise<MappingResponse> =>
  importRequest<MappingResponse>(opts, 'GET', batchPath(batchId, '/mapping'));

export const validateMapping = (
  opts: WebApiOpts,
  batchId: string,
  entityFamily: string,
  mapping: ColumnMapping,
): Promise<MappingValidation> =>
  importRequest<MappingValidation>(opts, 'POST', batchPath(batchId, '/mapping/validate'), {
    entityFamily,
    mapping,
  });

export const applyMapping = (
  opts: WebApiOpts,
  batchId: string,
  body: ApplyMappingBody,
): Promise<ApplyMappingResult> =>
  importRequest<ApplyMappingResult>(opts, 'PUT', batchPath(batchId, '/mapping'), body);

export const runPreview = (opts: WebApiOpts, batchId: string): Promise<BatchPreview> =>
  importRequest<BatchPreview>(opts, 'POST', batchPath(batchId, '/preview'), {});

/**
 * Queues the commit and answers 202 straight away; the rows are written in the
 * background and reported by `getCommitStatus`. A 409 carrying
 * `COMMIT_ALREADY_RUNNING` means a commit is already live on this batch — the
 * caller attaches to it instead of showing an error.
 *
 * `isDryRun` is REQUIRED and always sent (#517). The route treats an absent
 * flag as "keep the stored one", so after a test run a real import that left it
 * out would quietly run as another test run — the hauler would press Import,
 * see "nothing was saved", and have nothing saved. Sending `false` explicitly is
 * what turns a tested batch into a real import.
 */
export const commitImport = (
  opts: WebApiOpts,
  batchId: string,
  mode: CommitMode,
  isDryRun: boolean,
): Promise<CommitQueued> =>
  importRequest<CommitQueued>(opts, 'POST', batchPath(batchId, '/commit'), { mode, isDryRun });

/**
 * DEC-267: a test run is capped at 2,000 staged rows (`DRY_RUN_MAX_ROWS` in
 * api/src/import-commit/commit.ts). The route refuses over it with a 400; the
 * preview uses this to say so before the click.
 */
export const DRY_RUN_MAX_ROWS = 2000;

/**
 * The one commit refusal that means "watch it", not "it failed". `satisfies`
 * pins it to the shared catalog: a renamed code fails the typecheck here rather
 * than turning every double click into an error screen.
 */
export const COMMIT_ALREADY_RUNNING =
  'IMPORT_COMMIT_ALREADY_RUNNING' satisfies keyof typeof ERROR_CATALOG;

export const getCommitStatus = (opts: WebApiOpts, batchId: string): Promise<CommitStatus> =>
  importRequest<CommitStatus>(opts, 'GET', batchPath(batchId, '/commit-status'));

/** A refusal is a 200 with `complete: false`, not a thrown error. */
export const undoImport = (opts: WebApiOpts, batchId: string): Promise<UndoReport> =>
  importRequest<UndoReport>(opts, 'POST', batchPath(batchId, '/undo'), {});

export const requestErrorFile = (opts: WebApiOpts, batchId: string): Promise<ReportDownload> =>
  importRequest<ReportDownload>(opts, 'POST', batchPath(batchId, '/error-file'), {});

export const getResultFile = (opts: WebApiOpts, batchId: string): Promise<ReportDownload> =>
  importRequest<ReportDownload>(opts, 'GET', batchPath(batchId, '/result-file'));

/** §80 step 1: point each sheet at a template, or skip it (`entityFamily: null`). */
export const updateSheets = (
  opts: WebApiOpts,
  batchId: string,
  sheets: readonly SheetDecision[],
): Promise<SheetsUpdate> =>
  importRequest<SheetsUpdate>(opts, 'PATCH', batchPath(batchId, '/sheets'), { sheets });

/** 100 is the route's maximum page; the history shows the newest 100. */
export const HISTORY_LIMIT = 100;

export const listImports = (opts: WebApiOpts): Promise<{ batches: ImportBatchSummary[] }> =>
  importRequest<{ batches: ImportBatchSummary[] }>(
    opts,
    'GET',
    `/v1/imports?limit=${HISTORY_LIMIT.toString()}`,
  );

export const getImport = (opts: WebApiOpts, batchId: string): Promise<ImportBatchDetail> =>
  importRequest<ImportBatchDetail>(opts, 'GET', batchPath(batchId, ''));

export const listProfiles = (opts: WebApiOpts): Promise<{ profiles: MappingProfile[] }> =>
  importRequest<{ profiles: MappingProfile[] }>(opts, 'GET', '/v1/import-profiles');

/**
 * Answers 204 with no body, so it does not go through `importRequest`, which
 * parses JSON.
 *
 * It SENDS `{}`, like every other mutation here (DEC-201). The belief that a
 * body-less DELETE is safe was wrong on dev: from a real browser, this exact
 * call was refused with FST_ERR_CTP_EMPTY_JSON_BODY (400, reported as
 * VALIDATION_FAILED), because `authedFetch` always sets a JSON content-type.
 * Found by the 20.7 slice 3 E2E; unit tests mock fetch and cannot see it.
 */
export const deleteProfile = async (opts: WebApiOpts, profileId: string): Promise<void> => {
  const res = await authedFetch(opts, `/v1/import-profiles/${encodeURIComponent(profileId)}`, {
    method: 'DELETE',
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const { code, message } = await readErrorMessage(res);
    throw new ImportApiError(res.status, code, message);
  }
};
